import React, { useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart2, Home, Search, Zap, DatabaseZap, TrendingUp, LineChart, SearchCheck, MapPin } from 'lucide-react';
import { toast } from 'sonner';

// Components
import DynamicBackground from '@/components/DynamicBackground';
import AnimatedButton from '@/components/AnimatedButton';
import FeatureCard from '@/components/FeatureCard';
import AudienceSection from '@/components/AudienceSection';
import LocationIndicator from '@/components/LocationIndicator';
import PricingSection from '@/components/PricingSection';

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  useEffect(() => {
    // Check for section parameter in URL
    const searchParams = new URLSearchParams(location.search);
    const section = searchParams.get('section');
    if (section) {
      const sectionElement = document.getElementById(section);
      if (sectionElement) {
        // Use setTimeout to ensure the DOM is fully rendered
        setTimeout(() => {
          sectionElement.scrollIntoView({ behavior: 'smooth' });
          // Clean up the URL
          navigate('/', { replace: true });
        }, 100);
      }
    }
  }, [location, navigate]);
  
  const handlePropertyAnalysisClick = () => {
    navigate('/analisar-imovel');
    toast.info('Redirecionando para Análise de Imóveis');
  };
  const handleDashboardClick = () => {
    navigate('/home');
    toast.info('Redirecionando para o Dashboard');
  };
  return <div className="min-h-screen overflow-hidden">
      <DynamicBackground />
      
      {/* Hero Section */}
      <section id="hero" className="relative pt-16 md:pt-24 pb-12 md:pb-20">
        <div className="container mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-dark to-blue-light">
              Análise Inteligente do Mercado Imobiliário
            </h1>
            <p className="text-lg md:text-xl text-gray-700 mb-4 max-w-3xl mx-auto">
              Descubra o valor real dos imóveis e tome decisões baseadas em dados precisos sobre o mercado
            </p>
            
            <LocationIndicator />
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
              <AnimatedButton onClick={handlePropertyAnalysisClick} className="text-white bg-blue-main hover:bg-blue-dark px-6 py-3 rounded-lg shadow-md w-full sm:w-auto" delay={0.2}>
                <span>Analise de Imóvel Grátis</span>
                <Search className="h-5 w-5" />
              </AnimatedButton>
              
              <AnimatedButton onClick={handleDashboardClick} className="text-white bg-blue-dark hover:bg-blue-darker px-6 py-3 rounded-lg shadow-md w-full sm:w-auto" delay={0.4}>
                <span>Acesse os Dashboards</span>
                <BarChart2 className="h-5 w-5" />
              </AnimatedButton>
            </div>
          </motion.div>
        </div>
      </section>
      
      {/* Product Showcase Section */}
      <section className="py-12 md:py-20 ">
        <div className="container mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} viewport={{
          once: true
        }} className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4 text-blue-dark">O que é o Broker Base?</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Seu painel de controle inteligente do mercado imobiliário. Acesse relatórios, dashboards e ferramentas integradas com IA para tomar a melhor decisão ou auxiliar seus clientes.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Product Card 1 */}
            <motion.div initial={{
            opacity: 0,
            x: -30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }} className="bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg border border-gray-100">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-light/20 p-3 rounded-full">
                    <Search className="h-6 w-6 text-blue-dark" />
                  </div>
                  <h3 className="text-2xl font-bold text-blue-dark">Análise de Imóveis com IA</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Avalie qualquer imóvel e obtenha insights detalhados sobre preço, localização, 
                  comparativos de mercado e potencial de valorização.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2">
                    <Zap className="h-5 w-5 text-blue-main" />
                    <span className="text-gray-700">Análise instantânea de imóveis</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-main" />
                    <span className="text-gray-700">Comparativo de preços na região</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <Home className="h-5 w-5 text-blue-main" />
                    <span className="text-gray-700">Pontos fortes e fracos do imóvel</span>
                  </li>
                </ul>
                <AnimatedButton onClick={handlePropertyAnalysisClick} className="w-full justify-center">
                  <span>Analisar um Imóvel</span>
                  <ArrowRight className="h-4 w-4" />
                </AnimatedButton>
              </div>
            </motion.div>
            
            {/* Product Card 2 */}
            <motion.div initial={{
            opacity: 0,
            x: 30
          }} whileInView={{
            opacity: 1,
            x: 0
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} viewport={{
            once: true
          }} className="bg-white/90 backdrop-blur-sm rounded-2xl overflow-hidden shadow-lg border border-gray-100">
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="bg-blue-light/20 p-3 rounded-full">
                    <BarChart2 className="h-6 w-6 text-blue-dark" />
                  </div>
                  <h3 className="text-2xl font-bold text-blue-dark">Dashboards Imobiliários</h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Visualize dados completos do mercado imobiliário com gráficos interativos, tendências de preços
                  e análises por bairro ou tipo de imóvel.
                </p>
                <ul className="space-y-3 mb-6">
                  <li className="flex items-center gap-2">
                    <DatabaseZap className="h-5 w-5 text-blue-main" />
                    <span className="text-gray-700">Base de dados tratados e enriquecidos</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <LineChart className="h-5 w-5 text-blue-main" />
                    <span className="text-gray-700">Gráficos e visualizações interativas</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <TrendingUp className="h-5 w-5 text-blue-main" />
                    <span className="text-gray-700">Análises de anúncios e tamanho de mercado</span>
                  </li>
                </ul>
                <AnimatedButton onClick={handleDashboardClick} className="w-full justify-center bg-blue-dark hover:bg-blue-darker">
                  <span>Acessar Dashboards</span>
                  <ArrowRight className="h-4 w-4" />
                </AnimatedButton>
              </div>
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Features Section */}
      <section className="py-12 md:py-20 relative">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <motion.h2 initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 0.6
          }} viewport={{
            once: true
          }} className="text-3xl md:text-4xl font-bold mb-4 text-blue-dark">
              Recursos Principais
            </motion.h2>
            <motion.p initial={{
            opacity: 0
          }} whileInView={{
            opacity: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} viewport={{
            once: true
          }} className="text-gray-600 max-w-2xl mx-auto">
              As ferramentas a sua disposição para tomar a melhor decisão!
            </motion.p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <FeatureCard title="Análise Comparativa" description="Compare imóveis similares na região para tomar decisões mais fundamentadas" icon={<BarChart2 className="h-6 w-6 text-blue-main" />} delay={0} />
            <FeatureCard title="Preço por m²" description="Visualize o preço por metro quadrado dos imóveis em cada região" icon={<Home className="h-6 w-6 text-blue-main" />} delay={1} />
            <FeatureCard title="Tendências de Mercado" description="Acompanhe a evolução dos preços e identifique oportunidades de investimento" icon={<TrendingUp className="h-6 w-6 text-blue-main" />} delay={2} />
            <FeatureCard title="Análise de Bairros" description="Dados detalhados por bairro, destacando as áreas com maior valorização" icon={<Search className="h-6 w-6 text-blue-main" />} delay={3} className="sm:col-span-2 lg:col-span-1" />
            <FeatureCard title="Análise de Imobiliárias" description="Entenda as regiões e padrão de atuação das maiores imobiliárias do mercado" icon={<LineChart className="h-6 w-6 text-blue-main" />} delay={4} className="sm:col-span-2 lg:col-span-1" />
            <FeatureCard title="Encontre Oportunidades" description="Use os filtros avançados para encontrar oportunidades únicas" icon={<SearchCheck className="h-6 w-6 text-blue-main" />} delay={5} className="sm:col-span-2 lg:col-span-1" />
          </div>
        </div>
      </section>
      
      {/* New Pricing Section */}
      <PricingSection />
      
      {/* New Audience Section */}
      <AudienceSection />
      
      {/* CTA Section */}
      <section className="py-12 md:py-20 ">
        <div className="container mx-auto px-4">
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} className="bg-gradient-to-r from-blue-dark to-blue-main rounded-2xl p-8 md:p-12 text-center text-white max-w-4xl mx-auto shadow-lg">
            <h2 className="text-2xl md:text-4xl font-bold mb-4">
              Pronto para tomar decisões mais inteligentes sobre imóveis?
            </h2>
            <p className="text-blue-50 mb-8 text-lg max-w-2xl mx-auto">
              Comece agora a analisar imóveis ou explore nosso dashboard para conhecer melhor o mercado
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <AnimatedButton onClick={handlePropertyAnalysisClick} className="bg-white text-blue-dark hover:bg-blue-50 w-full sm:w-auto mx-auto sm:mx-0" delay={0.2}>
                <span>Analisar Imóvel</span>
                <Search className="h-5 w-5" />
              </AnimatedButton>
              <AnimatedButton onClick={handleDashboardClick} className="bg-blue-dark/40 text-white border border-white/20 hover:bg-blue-dark/60 w-full sm:w-auto mx-auto sm:mx-0" delay={0.4}>
                <span>Explorar Dashboard</span>
                <BarChart2 className="h-5 w-5" />
              </AnimatedButton>
            </div>
          </motion.div>
        </div>
      </section>
    </div>;
};
export default Index;
